import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Activity, AlertTriangle, Loader2 } from 'lucide-react'
import { Badge } from '@deepguide-ai/ui/badge'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { trpc } from '@/lib/trpc'
import {
  getStatusBadgeColor,
  getStatusLabel,
} from '@/lib/compliance-status'

type TestFilter =
  | 'ALL'
  | 'ATTENTION'
  | 'PENDING_REVIEW'
  | 'IN_PROGRESS'
  | 'NOT_STARTED'
  | 'NOT_APPLICABLE'
  | 'PASSED'

const ATTENTION_STATUSES = new Set([
  'FAILED',
  'NEEDS_RETEST',
  'BLOCKED',
  'INCONCLUSIVE',
])
const PENDING_REVIEW_STATUSES = new Set(['SUBMITTED', 'PASSED'])

function filterForStatus(status: string): TestFilter {
  if (ATTENTION_STATUSES.has(status)) return 'ATTENTION'
  if (PENDING_REVIEW_STATUSES.has(status)) return 'PENDING_REVIEW'
  if (status === 'IN_PROGRESS') return 'IN_PROGRESS'
  if (status === 'NOT_STARTED') return 'NOT_STARTED'
  if (status === 'NOT_APPLICABLE') return 'NOT_APPLICABLE'
  if (status === 'APPROVED') return 'PASSED'
  return 'ALL'
}

const FILTER_LABEL: Record<TestFilter, string> = {
  ALL: 'All',
  ATTENTION: 'Needs attention',
  PENDING_REVIEW: 'Pending review',
  IN_PROGRESS: 'In progress',
  NOT_STARTED: 'Not started',
  NOT_APPLICABLE: 'Not applicable',
  PASSED: 'Passed',
}

const FILTER_ORDER: TestFilter[] = [
  'ALL',
  'ATTENTION',
  'PENDING_REVIEW',
  'IN_PROGRESS',
  'NOT_STARTED',
  'NOT_APPLICABLE',
  'PASSED',
]

const STATUS_ORDER: Record<TestFilter, number> = {
  ATTENTION: 0,
  PENDING_REVIEW: 1,
  IN_PROGRESS: 2,
  NOT_STARTED: 3,
  NOT_APPLICABLE: 4,
  PASSED: 5,
  ALL: 6,
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className='text-muted-foreground text-[10px] font-semibold tracking-wider uppercase'>
      {children}
    </div>
  )
}

/** The policy's Health tab: the status of the tests that verify its claims
 *  (2026-09-30: drift / gap findings and claim coverage are no longer shown
 *  here). */
export function PolicyHealthPanel({
  workspaceId,
  programId,
  policySlug,
  policyDocumentId,
}: {
  workspaceId: string
  programId: string
  policySlug: string
  policyDocumentId?: string
}) {
  const [filter, setFilter] = useState<TestFilter>('ALL')
  const healthQuery = useQuery({
    ...trpc.complianceProgram.getPolicyHealth.queryOptions({
      programId,
      policySlug,
      policyDocumentId,
    }),
    enabled: !!programId && !!policySlug,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })

  if (healthQuery.isLoading) {
    return (
      <div className='flex flex-1 items-center justify-center p-4'>
        <Loader2 className='text-muted-foreground size-4 animate-spin' />
      </div>
    )
  }

  if (healthQuery.isError) {
    return (
      <div className='flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10 text-center'>
        <AlertTriangle className='text-status-orange size-10' strokeWidth={1.5} />
        <p className='text-foreground mt-4 text-sm font-medium'>
          Unable to load policy health
        </p>
        <p className='text-muted-foreground mt-1 max-w-[260px] text-xs leading-relaxed'>
          {healthQuery.error?.message ??
            'Refresh the page or try again in a moment.'}
        </p>
      </div>
    )
  }

  const testHealth = healthQuery.data?.testHealth
  const tests = [...(testHealth?.tests ?? [])].sort(
    (a, b) =>
      STATUS_ORDER[filterForStatus(a.effectiveStatus)] -
        STATUS_ORDER[filterForStatus(b.effectiveStatus)] ||
      (a.taskCode ?? '').localeCompare(b.taskCode ?? '')
  )
  const visibleTests =
    filter === 'ALL'
      ? tests
      : tests.filter((test) => filterForStatus(test.effectiveStatus) === filter)
  const filterCounts = tests.reduce<Record<TestFilter, number>>(
    (counts, test) => {
      counts.ALL++
      const group = filterForStatus(test.effectiveStatus)
      if (group !== 'ALL') counts[group]++
      return counts
    },
    {
      ALL: 0,
      ATTENTION: 0,
      PENDING_REVIEW: 0,
      IN_PROGRESS: 0,
      NOT_STARTED: 0,
      NOT_APPLICABLE: 0,
      PASSED: 0,
    }
  )

  return (
    <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block'>
      <div className='space-y-5 p-4'>
        <section className='space-y-3'>
          <div className='flex items-center justify-between'>
            <SectionLabel>Tests</SectionLabel>
            <span className='text-muted-foreground text-[10px] font-medium tabular-nums'>
              {tests.length}
            </span>
          </div>

          {tests.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {FILTER_ORDER.map((item) =>
                item === 'ALL' || filterCounts[item] > 0 ? (
                  <button
                    key={item}
                    type='button'
                    onClick={() => setFilter(item)}
                    className={`rounded-sm border px-2 py-1 text-[10px] font-medium transition-colors ${
                      filter === item
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    {FILTER_LABEL[item]} {filterCounts[item]}
                  </button>
                ) : null
              )}
            </div>
          )}

          {visibleTests.length > 0 ? (
            <div className='overflow-hidden rounded-md border'>
              {visibleTests.map((test) => {
                const color = getStatusBadgeColor(test.effectiveStatus)
                return (
                  <Link
                    key={test.id}
                    to='/workspaces/$workspaceId/tests/$testId'
                    params={{ workspaceId, testId: test.id }}
                    search={{ from: 'screenata-tests' as any }}
                    className='hover:bg-muted/50 flex items-center justify-between gap-3 border-b p-3 transition-colors last:border-b-0'
                  >
                    <span className='min-w-0'>
                      <span className='flex items-center gap-1.5'>
                        {test.taskCode && (
                          <span className='text-muted-foreground shrink-0 font-mono text-[10px]'>
                            {test.taskCode}
                          </span>
                        )}
                        <span className='text-foreground truncate text-xs font-medium'>
                          {test.title ?? 'Untitled test'}
                        </span>
                      </span>
                      <span className='text-muted-foreground mt-1 block text-[10px]'>
                        Covers {test.claimCount} claim
                        {test.claimCount === 1 ? '' : 's'}
                      </span>
                    </span>
                    <Badge status={color}>
                      {getStatusLabel(test.effectiveStatus)}
                    </Badge>
                  </Link>
                )
              })}
            </div>
          ) : tests.length === 0 ? (
            <div className='flex flex-col items-center rounded-md border px-6 py-10 text-center'>
              <Activity className='text-muted-foreground size-8' strokeWidth={1.5} />
              <p className='text-foreground mt-3 text-xs font-medium'>
                No linked tests yet
              </p>
              <p className='text-muted-foreground mt-1 text-[11px] leading-relaxed'>
                Link tests from Claims to start tracking this policy.
              </p>
            </div>
          ) : (
            <p className='text-muted-foreground rounded-md border p-4 text-center text-xs'>
              No tests match this status.
            </p>
          )}
        </section>
      </div>
    </ScrollArea>
  )
}
